import { Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Sparkles, Settings, LogOut, User, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/hooks/useProfile';
import { ADMIN_ORG_ID, db } from '@/lib/db';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { profile, organization, isLoading: isProfileLoading } = useProfile();

  const isAdmin = !isProfileLoading && profile?.org_id === ADMIN_ORG_ID;

  const handleLogout = async () => {
    try {
      await db.auth.signOut();
      toast.success('已退出登录');
    } catch (error) {
      console.error('退出登录失败:', error);
      toast.error('退出登录失败，请重试');
    }
  };

  const navSections = [
    {
      title: '项目',
      items: [
        {
          title: '项目列表',
          href: '/',
          icon: LayoutGrid,
        },
      ],
    },
    {
      title: '笔记风格',
      items: [
        {
          title: '风格库',
          href: '/note-styles',
          icon: Sparkles,
        },
      ],
    },
    ...(isAdmin ? [
      {
        title: '系统管理',
        items: [
          {
            title: '模板工作台',
            href: '/template-workbench',
            icon: Settings,
          },
        ],
      },
    ] : []),
  ];

  return (
    <div className="w-48 border-r bg-background h-screen flex flex-col fixed left-0 top-0 z-30">
      <div className="p-3 py-6">
        <Link to="/" className="flex items-center justify-center">
          <img src="/logo.png" alt="Laigu AI" className="h-10 w-auto object-contain" />
        </Link>
      </div>
      
      <nav className="flex-1 px-2.5 space-y-4">
        {navSections.map((section) => (
          <div key={section.title} className="space-y-1">
            <p className="text-[10px] font-medium text-muted-foreground/60 px-2.5 uppercase tracking-wider">{section.title}</p>
            {section.items.map((item) => (
              <Button
                key={item.href}
                variant={item.href === pathname || (item.href !== '/' && pathname.startsWith(item.href)) ? 'secondary' : 'ghost'}
                className={cn(
                  "w-full justify-start gap-2.5 h-9 text-[13px] font-normal px-2.5",
                item.href === '/' && pathname === '/' && "bg-secondary text-secondary-foreground"
                )}
              asChild
              >
              <Link to={item.href}>
                <item.icon className="w-3.5 h-3.5 opacity-70" />
                {item.title}
              </Link>
              </Button>
            ))}
          </div>
        ))}
      </nav>

      {/* 账号信息卡片 */}
      {!isProfileLoading && profile && (
        <div className="p-2 border-t">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full h-auto p-1.5 flex items-center gap-2 justify-start hover:bg-secondary/50 rounded-xl transition-colors">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary border border-primary/20">
                  <User className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 overflow-hidden text-left">
                  <div className="flex items-center gap-1 text-[8px] text-muted-foreground/70 uppercase tracking-tight font-medium">
                    <Building2 className="w-1.5 h-1.5" />
                    <span className="truncate">{organization?.name || '个人账号'}</span>
                  </div>
                  <p className="text-[11px] font-medium truncate text-foreground leading-tight">
                    {profile.email.split('@')[0]}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-44 mb-2">
              <div className="px-2 py-2 flex flex-col gap-0.5">
                <p className="text-xs font-medium text-foreground">当前账号</p>
                <p className="text-[11px] text-muted-foreground truncate">{profile.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer">
                <LogOut className="w-3.5 h-3.5 mr-2" />
                退出登录
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

